import React from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { getIsSamf } from '../../../../state/models/society'
import { theme } from '../../../../theme/theme'
import { Icon, IconChoices } from '../../../common/Icon'
import { Spinner } from '../../../common/Spinner'
import { useCurrentSociety } from '../../../../hooks/useCurrentSociety'
import { useToastNotifications } from '../../../../hooks/useToastNotification'
import { ToastType } from '../../../common/Toast/toast-type'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { useModal } from '../../../../hooks/useModal'
import { ConfirmationModal } from '../../../common/ConfirmationModal'
import { Button, ButtonVariant } from '../../../common/Button'
import { ExtendedUnitModel, getUnitString } from '../../../../helpers/units'

import {
  removePreApprovedUser,
  sendPreApprovedUserInviteReminder,
} from '../../../../api/societies'
import { IPreApprovedList } from '../../../../interfaces/models/societies.interfaces'
import { entranceQueries } from '../../../../api/society-entrances'
import { unitQueries } from '../../../../api/units'
import { translateRole } from '../../../../helpers/society'
import { getUnitRole } from '../../../../helpers/translations/unit-roles'

interface PreApprovedListItemProps {
  preApproved: IPreApprovedList
  disableButtons?: boolean
  className?: string
}

export const PreApprovedListItem = ({
  preApproved,
  disableButtons,
  className,
}: PreApprovedListItemProps): React.ReactNode => {
  const { translate } = useAppTranslation()
  const { society } = useCurrentSociety()
  const { data: societyEntrances } = useQuery(
    entranceQueries.getEntrancesPerSociety(society?._id)
  )
  const { data: societyUnits } = useQuery(
    unitQueries.getUnitsPerSociety(society?._id)
  )
  const { setToastNotification } = useToastNotifications()
  const {
    show: showDeleteConfirmationModal,
    open: openDeleteConfirmationModal,
    close: closeDeleteConfirmationModal,
  } = useModal()
  const {
    show: showRemindConfirmationModal,
    open: openRemindConfirmationModal,
    close: closeRemindConfirmationModal,
  } = useModal()

  const {
    mutate: mutateSendPreApprovedUserInviteReminder,
    isPending: isSendingReminder,
    isSuccess: reminderSent,
  } = useMutation({
    mutationFn: sendPreApprovedUserInviteReminder,
    onSuccess: () => {
      setToastNotification(
        ToastType.SUCCESS,
        translate('preApproved.flashMessage.sendReminderSuccess')
      )
    },
    onError: () => {
      setToastNotification(
        ToastType.DANGER,
        translate('preApproved.flashMessage.sendReminderFailure')
      )
    },
  })
  const sendReminder = async (): Promise<void> => {
    society &&
      mutateSendPreApprovedUserInviteReminder({
        id: society._id,
        email: preApproved.userEmail,
      })
  }

  const { mutate: mutateRemovePreApprovedUser } = useMutation({
    mutationFn: removePreApprovedUser,
    onSuccess: () => {
      setToastNotification(
        ToastType.SUCCESS,
        translate('preApproved.flashMessage.deletePreApprovedSuccess')
      )
    },
    onError: () => {
      setToastNotification(
        ToastType.DANGER,
        translate('preApproved.flashMessage.deletePreApprovedFailure')
      )
    },
  })
  const deletePreApproved = async (): Promise<void> => {
    society &&
      mutateRemovePreApprovedUser({
        id: society._id,
        body: {
          unitId: preApproved.unitId,
          userEmail: preApproved.userEmail,
        },
      })
  }

  const renderReminderStatus = (): JSX.Element => {
    if (reminderSent) {
      return <Icon icon={IconChoices.CHECKMARK} />
    }
    if (isSendingReminder) {
      return <Spinner />
    }
    return (
      <Button
        icon={IconChoices.NOTIFICATION_ALERT}
        size="xs"
        variant={ButtonVariant.TEXT}
        onClick={openRemindConfirmationModal}
        disabled={disableButtons}
      />
    )
  }

  const isSamf = getIsSamf(society)

  const unit = societyUnits?.find((_unit) => preApproved.unitId === _unit._id)
  const entrance = societyEntrances?.find(
    (_entrance) => unit?.entranceId === _entrance._id
  )
  const unitString =
    unit && getUnitString({ ...unit, entrance } as ExtendedUnitModel, isSamf)

  return (
    <>
      <div
        className={`border-1 flex items-center justify-between border-t py-4 ${
          className ?? ''
        }`}
      >
        <div>
          <div className="flex space-x-1">
            <p style={theme.textVariants.baseBold}>{preApproved.userEmail}</p>
            <p style={theme.textVariants.base} className="text-neutral-40">
              ({unitString})
            </p>
          </div>
          <div className="flex">
            {`Inbjuden som ${preApproved.role ? translate(getUnitRole(preApproved.role) as string) : ''}`}
            {preApproved.boardRole &&
              `, ${translateRole(preApproved.boardRole?.toString())}`}
            {preApproved.adminRole === '' && `, ${translateRole('admin')}`}
          </div>
        </div>
        <div className="flex">
          {renderReminderStatus()}
          <Button
            icon={IconChoices.DELETE}
            iconColor={theme.colors.red}
            size="xs"
            variant={ButtonVariant.TEXT}
            onClick={openDeleteConfirmationModal}
            disabled={disableButtons}
          />
        </div>
      </div>
      {showDeleteConfirmationModal && (
        <ConfirmationModal
          title={translate('preApproved.confirmationDialog.deleteTitle')}
          description={translate(
            'preApproved.confirmationDialog.deleteDescription'
          )}
          show={showDeleteConfirmationModal}
          close={closeDeleteConfirmationModal}
          onConfirm={deletePreApproved}
        />
      )}
      {showRemindConfirmationModal && (
        <ConfirmationModal
          title={translate('preApproved.confirmationDialog.remindTitle')}
          description={translate(
            'preApproved.confirmationDialog.remindDescription'
          )}
          show={showRemindConfirmationModal}
          close={closeRemindConfirmationModal}
          onConfirm={sendReminder}
          confirmationButtonLabel={translate('common.actions.send')}
        />
      )}
    </>
  )
}
